import { useRef, useState, useEffect } from "react";

export default function BeforeAfterSlider({ beforeSrc, afterSrc, label }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragRef = useRef(false);

  const updatePos = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.min(Math.max(0, clientX - rect.left), rect.width);
    setPos((x / rect.width) * 100);
  };

  const onDown = (e) => {
    dragRef.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    updatePos(clientX);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      updatePos(clientX);
    };
    const onUp = () => (dragRef.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative w-full aspect-[16/10] overflow-hidden rounded-sm select-none cursor-ew-resize bg-sand-200"
      onMouseDown={onDown}
      onTouchStart={onDown}
    >
      {/* After (base) */}
      <img src={afterSrc} alt="Nachher" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <span className="absolute top-4 right-4 z-10 px-3 py-1 bg-sand-100/90 backdrop-blur text-xs uppercase tracking-[0.2em] font-bold text-stone-900 rounded-sm">Nachher</span>

      {/* Before (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={beforeSrc}
          alt="Vorher"
          className="absolute inset-0 h-full object-cover"
          style={{ width: `${containerRef.current?.offsetWidth || 1000}px`, maxWidth: "none" }}
          draggable={false}
        />
        <span className="absolute top-4 left-4 z-10 px-3 py-1 bg-stone-900/90 text-sand-100 text-xs uppercase tracking-[0.2em] font-bold rounded-sm">Vorher</span>
      </div>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-sand-100 pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-1px)" }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-sand-100 rounded-full flex items-center justify-center shadow-lg border border-sand-300">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-stone-900">
            <polyline points="15 18 9 12 15 6" />
            <polyline points="9 18 3 12 9 6" transform="translate(12 0)" />
          </svg>
        </div>
      </div>

      {label && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-sand-100/90 backdrop-blur text-stone-900 text-sm font-display rounded-sm">
          {label}
        </div>
      )}
    </div>
  );
}
